/**
 * ESA WorldCover classes, grouped into the terrain types we show, and the four types only
 * OpenStreetMap can tell (`osm.ts`, docs/features/play-world-osm.md §3).
 *
 * Each pixel is a class code, not a measurement. The refinements use the heights we already
 * have: bare ground steeper than 35° is reliably a cliff or rock face; on flatter ground it is
 * more likely scree, gravel or sand. And in a Mediterranean shrubland (`detectBiome`: fynbos,
 * chaparral, maquis, mallee), WorldCover's tree cover on open slopes is mostly tall shrubs, so
 * it becomes scrub there; mapped woods and plantations come back as forest from OpenStreetMap.
 */

import type { TerrainPalette } from '@/lib/virtual-course/palette'
import { cellAt, type Grid } from './grid'

export type TerrainType =
  | 'forest'
  | 'rock'
  | 'scree'
  | 'water'
  | 'snow'
  | 'grass'
  | 'wetland'
  | 'built'
  | 'farmland'
  | 'sand'
  | 'vineyard'
  | 'orchard'
  | 'lawn'
  | 'scrub'

export const TERRAIN_TYPES: { id: TerrainType; label: string; classes: string }[] = [
  { id: 'forest', label: 'Forest', classes: 'Tree cover (10), Mangroves (95)' },
  { id: 'grass', label: 'Grass, heath', classes: 'Grassland (30), Moss and lichen (100)' },
  { id: 'farmland', label: 'Farmland', classes: 'Cropland (40)' },
  { id: 'built', label: 'Towns, buildings', classes: 'Built-up (50)' },
  { id: 'scree', label: 'Scree, gravel, sand', classes: 'Bare / sparse vegetation (60) under 35°' },
  { id: 'rock', label: 'Cliff, rock face', classes: 'Bare / sparse vegetation (60) at 35° or steeper' },
  { id: 'snow', label: 'Snow, glaciers', classes: 'Snow and ice (70)' },
  { id: 'water', label: 'Lakes, sea, wide rivers', classes: 'Permanent water bodies (80)' },
  { id: 'wetland', label: 'Wetland, bog', classes: 'Herbaceous wetland (90)' },
  { id: 'sand', label: 'Beach, dunes', classes: 'OpenStreetMap natural=beach, natural=sand' },
  { id: 'vineyard', label: 'Vineyard', classes: 'OpenStreetMap landuse=vineyard' },
  { id: 'orchard', label: 'Orchard', classes: 'OpenStreetMap landuse=orchard' },
  { id: 'lawn', label: 'Park, golf course, playing field', classes: 'OpenStreetMap leisure=park, golf_course, pitch, garden; landuse=grass' },
  { id: 'scrub', label: 'Shrubland, fynbos, maquis', classes: 'Shrubland (20); tree cover on open slopes of a Mediterranean shrubland; OpenStreetMap natural=scrub' },
]

export const CLIFF_SLOPE_DEGREES = 35

/** The highest code a world pack's `types` grid holds: WorldCover's nine types, which every reader draws. */
export const BASE_TYPE_CODES = 9
/**
 * The highest code a world pack's `refinedTypes` holds: OpenStreetMap's first four types, which every reader since
 * them draws. Later types (scrub) go in `newerTypes`, so such a reader draws their base type, not a code it can't.
 */
export const REFINED_TYPE_CODES = 13

/** What a type past the nine is drawn as by a reader that knows only those (world packs, `worldPack.ts`). */
export const BASE_TYPE_OF: Record<'sand' | 'vineyard' | 'orchard' | 'lawn' | 'scrub', TerrainType> = {
  sand: 'scree',
  vineyard: 'farmland',
  orchard: 'farmland',
  lawn: 'grass',
  scrub: 'grass',
}

/**
 * A world's biome, where it changes what grows: `mediterranean` for a Mediterranean shrubland (dry summers, low
 * sclerophyll shrubs: fynbos, chaparral, maquis, mallee), else null.
 */
export type Biome = 'mediterranean' | null

/** Latitudes (either side of the equator) where Mediterranean shrublands lie. */
export const MEDITERRANEAN_LATITUDES: readonly [number, number] = [28, 46]
/** WorldCover's shrubland as a share of the vegetated land (trees, shrubs, grass, crops) that makes one. */
export const MEDITERRANEAN_SHRUB_SHARE = 0.15
/** In one, tree cover on ground at least this steep is tall shrubland, not woodland. */
export const SHRUBLAND_TREE_SLOPE_DEGREES = 10

/**
 * The biome from where the box lies and what WorldCover sees in it: a Mediterranean shrubland lies between 28° and
 * 46° north or south and has a good share of shrubland among its vegetation. Not one region's rule: the same test
 * finds the Cape's fynbos, California's chaparral, the Mediterranean's maquis and south-west Australia's mallee.
 */
export function detectBiome(latitude: number, cover: ArrayLike<number>): Biome {
  const [from, to] = MEDITERRANEAN_LATITUDES
  if (Math.abs(latitude) < from || Math.abs(latitude) > to) return null
  let shrubs = 0
  let vegetated = 0
  for (let index = 0; index < cover.length; index++) {
    const code = cover[index]
    if (code === 20) shrubs += 1
    if (code === 10 || code === 20 || code === 30 || code === 40) vegetated += 1
  }
  return vegetated > 0 && shrubs / vegetated >= MEDITERRANEAN_SHRUB_SHARE ? 'mediterranean' : null
}

/**
 * Codes 1–13 index `TERRAIN_TYPES` + 1; 0 means no data. Fits the refined grid in a byte. Codes 1–9
 * come from WorldCover; 10–13 only from OpenStreetMap.
 */
export const TERRAIN_CODE: Record<TerrainType, number> = Object.fromEntries(
  TERRAIN_TYPES.map((type, index) => [type.id, index + 1]),
) as Record<TerrainType, number>

/**
 * Terrain type for one WorldCover pixel. Missing tiles (open sea) read as 0, which we show
 * as water: WorldCover covers every land pixel on Earth.
 */
export function terrainTypeFor(worldCoverClass: number, slope: number): TerrainType {
  switch (worldCoverClass) {
    case 10:
    case 95:
      return 'forest'
    case 20:
      return 'scrub'
    case 30:
    case 100:
      return 'grass'
    case 40:
      return 'farmland'
    case 50:
      return 'built'
    case 60:
      return slope >= CLIFF_SLOPE_DEGREES ? 'rock' : 'scree'
    case 70:
      return 'snow'
    case 90:
      return 'wetland'
    case 0:
    case 80:
    default:
      return 'water'
  }
}

/** Refine the WorldCover grid with slope (looked up from the coarser height grid) and the biome. */
export function refineTerrainTypes(cover: Grid<Uint8Array>, slope: Grid<Float32Array>, biome: Biome = null): Grid<Uint8Array> {
  const types = new Uint8Array(cover.values.length)
  const shrubland = biome === 'mediterranean'
  for (let row = 0; row < cover.rows; row += 1) {
    const v = (row + 0.5) / cover.rows
    for (let column = 0; column < cover.cols; column += 1) {
      const index = row * cover.cols + column
      const code = cover.values[index]
      const steepness = code === 60 || (shrubland && code === 10) ? slope.values[cellAt(slope, (column + 0.5) / cover.cols, v)] : 0
      types[index] = shrubland && code === 10 && steepness >= SHRUBLAND_TREE_SLOPE_DEGREES ? TERRAIN_CODE.scrub : TERRAIN_CODE[terrainTypeFor(code, steepness)]
    }
  }
  return { ...cover, values: types }
}

export function terrainTypeOfCode(code: number): TerrainType | null {
  return TERRAIN_TYPES[code - 1]?.id ?? null
}

/** Terrain colours come from the Play terrain tokens; snow takes the canvas white. */
export function terrainColors(palette: TerrainPalette): Record<TerrainType, string> {
  return {
    forest: palette.tree,
    grass: palette.grass,
    farmland: palette.veld,
    built: palette.earth,
    scree: palette.sand,
    rock: palette.rock,
    snow: palette.canvas,
    water: palette.water,
    wetland: palette.grassDeep,
    sand: palette.sand,
    vineyard: palette.grassDeep,
    orchard: palette.treeLight,
    lawn: palette.grass,
    scrub: palette.grassDeep,
  }
}

export interface TerrainShare {
  type: TerrainType
  label: string
  share: number
}

/** Share of each terrain type across the box, largest first, empty types left out. */
export function terrainShares(types: Grid<Uint8Array>): TerrainShare[] {
  const counts = new Array<number>(TERRAIN_TYPES.length + 1).fill(0)
  for (let index = 0; index < types.values.length; index += 1) counts[types.values[index]] += 1
  const total = types.values.length
  return TERRAIN_TYPES.map((type) => ({ type: type.id, label: type.label, share: counts[TERRAIN_CODE[type.id]] / total }))
    .filter((entry) => entry.share > 0)
    .sort((a, b) => b.share - a.share)
}
